import React, { useState } from 'react';
import { CloudUpload, CheckCircle, AlertCircle, X, Trash2, Loader2, ChevronDown, RefreshCw } from 'lucide-react';
import { useUpload, UploadTask } from '../../contexts/UploadContext';
import { useTranslation } from 'react-i18next';

export const MediaNotificationCenter: React.FC = () => {
    const { tasks, removeTask, clearCompleted, retryTask, replaceFileAndRetry } = useUpload();
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const activeTasks = tasks.filter(t => t.status === 'uploading');
    const hasActive = activeTasks.length > 0;

    if (tasks.length === 0) return null;

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-2.5 rounded-full transition-all relative ${hasActive ? 'bg-primary/10 text-primary animate-pulse' : 'hover:bg-v3-bg text-v3-text-secondary'}`}
            >
                <CloudUpload className="w-6 h-6" />
                {tasks.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-[10px] font-bold rounded-full border-2 border-white flex items-center justify-center">
                        {tasks.length}
                    </span>
                )}
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)} />
                    <div className={`absolute top-full mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-v3-border z-40 overflow-hidden animate-in fade-in slide-in-from-top-2 ${i18n.language === 'ar' ? 'left-0' : 'right-0'}`}>
                        <div className="p-4 border-b border-v3-border flex items-center justify-between bg-v3-bg/50">
                            <h4 className="font-bold text-sm text-v3-text">{t('media.uploads') || 'عمليات الرفع'}</h4>
                            <button
                                onClick={clearCompleted}
                                className="text-[10px] text-v3-text-secondary hover:text-red-500 font-medium transition-colors"
                            >
                                {t('common.clear_all') || 'مسح المكتمل'}
                            </button>
                        </div>

                        <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                            <input
                                type="file"
                                id="replace-file-input"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    const taskId = (e.target as any).dataset.taskId;
                                    if (file && taskId) {
                                        replaceFileAndRetry(taskId, file);
                                    }
                                    e.target.value = ''; // Reset for same file selection
                                }}
                            />
                            {tasks.map((task) => (
                                <div key={task.id} className="p-4 border-b border-v3-border last:border-0 hover:bg-v3-bg/30 transition-colors group">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1 min-w-0 pr-3">
                                            <p className="text-xs font-bold text-v3-text truncate">{task.fileName}</p>
                                            <p className="text-[10px] text-v3-text-secondary mt-0.5">ID: {task.propertyId}</p>
                                        </div>
                                        <button
                                            onClick={() => removeTask(task.id)}
                                            className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all duration-300 ${task.status === 'error' ? 'bg-red-500' :
                                                    task.status === 'success' ? 'bg-green-500' : 'bg-primary'
                                                    }`}
                                                style={{ width: `${task.progress}%` }}
                                            />
                                        </div>
                                        <span className="text-[10px] font-bold text-v3-text-secondary w-8 text-right">
                                            {Math.round(task.progress)}%
                                        </span>
                                    </div>

                                    {task.status === 'error' && (
                                        <div className="flex flex-col gap-2 mt-2">
                                            <p className="text-[10px] text-red-500 flex items-center gap-1 flex-1 truncate">
                                                <AlertCircle className="w-3 h-3" />
                                                {task.error}
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => retryTask(task.id)}
                                                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-[10px] font-bold"
                                                >
                                                    <RefreshCw className="w-3 h-3" />
                                                    {t('common.retry') || 'إعادة'}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const input = document.getElementById('replace-file-input') as HTMLInputElement;
                                                        if (input) {
                                                            (input as any).dataset.taskId = task.id;
                                                            input.click();
                                                        }
                                                    }}
                                                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-v3-bg text-v3-text hover:bg-v3-border transition-colors text-[10px] font-bold"
                                                >
                                                    <CloudUpload className="w-3 h-3" />
                                                    {t('common.edit') || 'تعديل'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {task.status === 'success' && (
                                        <p className="text-[10px] text-green-600 mt-2 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" />
                                            {t('common.success') || 'تم الرفع بنجاح'}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
